import { Link } from "react-router-dom";
import { Bank, Brain, Database, LockKey, ShieldCheck, Trash } from "@phosphor-icons/react";
import { SiStripe, SiQuickbooks } from "react-icons/si";
import { usePageMeta, PAGE_META } from "@/lib/pageMeta";
import { SiteFooter, SiteNav } from "@/components/landing/SiteChrome";

const SOURCES = [
  [Bank, "#00FF9D", "Bank (via Plaid)", "Read-only. You sign in to your bank through Plaid, so CalibRunway never sees your bank login. We read balances and transactions."],
  [SiStripe, "#635BFF", "Stripe", "Stripe's connection screen asks for full access because it no longer offers read-only access for new connections. CalibRunway only reads subscriptions and payments to measure revenue. It never creates charges, refunds or changes settings."],
  [SiQuickbooks, "#2CA01C", "QuickBooks Online", "We read bills, invoices and vendors to measure expenses, money owed to you and money you owe. We never create or edit anything in your books."],
];

const POINTS = [
  [ShieldCheck, "What we never do", "Move money, charge your customers, change your books, or sell or rent your data to anyone."],
  [LockKey, "How connections are stored", "Connection keys for Stripe, your bank and QuickBooks are stored encrypted. You can disconnect any source from Integrations at any time: the stored keys are deleted, and Stripe and Plaid are told to end access."],
  [Database, "Where your data lives", "In CalibRunway's application database on our cloud hosting, only to run your workspace: your forecast, scenarios, reports and team notes."],
  [Brain, "The AI Calibrator", "When you ask a what-if, your question and the figures needed to answer it go to our AI provider to write the answer. Every result is shown as a separate scenario and never replaces your baseline."],
  [Trash, "Your control", "Download a copy of your data or delete your account from Account & privacy. Deletion removes your login, connections and workspace data."],
];

export default function Security() {
  usePageMeta(PAGE_META.security);
  return (
    <div className="bg-[#050505] min-h-screen" data-testid="security-page">
      <SiteNav />
      <main className="mx-auto max-w-5xl px-6 py-20 md:py-28">
        <p className="font-mono text-xs text-[#00FF9D] mb-4">// SECURITY & DATA</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight max-w-3xl">We read your numbers. We never move your money.</h1>
        <p className="mt-6 max-w-2xl text-[#A3A3A3] leading-relaxed">We read your Stripe, bank and QuickBooks data to build your runway model. We never move money or sell your data. Here is exactly what each connection can see.</p>
        <section className="mt-14 grid md:grid-cols-3 gap-6">
          {SOURCES.map(([I, c, name, body]) => (
            <div key={name} data-testid={`security-source-${name.split(" ")[0].toLowerCase()}`} className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-6">
              <I size={22} style={{ color: c }} />
              <h2 className="font-display text-lg mt-4">{name}</h2>
              <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{body}</p>
            </div>
          ))}
        </section>
        <section className="mt-14 divide-y divide-[#2A2A2A] border-y border-[#2A2A2A]">
          {POINTS.map(([I, title, body]) => (
            <div key={title} className="py-6 grid md:grid-cols-12 gap-3">
              <h2 className="md:col-span-4 font-display text-lg flex items-center gap-3"><I size={20} className="text-[#00FF9D]" />{title}</h2>
              <p className="md:col-span-8 text-sm text-[#A3A3A3] leading-relaxed">{body}</p>
            </div>
          ))}
        </section>
        <p className="mt-10 text-sm text-[#A3A3A3]">Signing in supports two-step verification, and team roles control who can change what. See also our <Link to="/terms" className="text-[#00FF9D] underline">Terms</Link>.</p>
      </main>
      <SiteFooter />
    </div>
  );
}
